import { useState, useEffect } from "react";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import axios from "axios";
import { resetFlagOnNewLogin } from "helpers/resetFlagOnNewLogin";

const limitHourInWeek = 12;

function verificaAutoavaliacaoSemana(colaborador_id: number) {
  return axios.get(`/autoavaliacao-resposta/verifica/${colaborador_id}`);
}

export function responderAutoavaliacaoBatch(
  data: {
    autoavaliacaocampo_id: number | null;
    resposta: number | null;
    observacao: string;
  }[],
) {
  return axios.post("/autoavaliacao-resposta", data);
}

export function useWeeklyAutoavaliacao() {
  const { user } = useSelector((state: any) => ({
    user: state.Auth.user,
  }));

  const [status, setStatus] = useState<
    "loading" | "open" | "button" | "hidden"
  >("loading");

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const colaborador_id = user?.usuario?.colaborador?.id;
  const token = user?.token;

  useEffect(() => {
    let hideTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let showTimeoutId: ReturnType<typeof setTimeout> | null = null;

    resetFlagOnNewLogin("autoavaliacao", "autoavaliacaoClosed", token);

    async function fetchStatus() {
      try {
        const response = await verificaAutoavaliacaoSemana(colaborador_id);
        const answered = !!response.data;

        setIsLoading(false);

        const now = dayjs();
        const day = now.day();
        const hour = now.hour();

        const scheduleTimeouts = () => {
          let nextThurNoon = now
            .day(4)
            .hour(limitHourInWeek)
            .minute(0)
            .second(0)
            .millisecond(0);

          if (nextThurNoon.isBefore(now)) {
            nextThurNoon = nextThurNoon.add(1, "week");
          }

          hideTimeoutId = setTimeout(() => {
            sessionStorage.removeItem("autoavaliacaoClosed");
            setStatus("hidden");
          }, nextThurNoon.diff(now));

          let nextFriMidnight = now.day(5).startOf("day");

          if (nextFriMidnight.isBefore(now) || nextFriMidnight.isSame(now)) {
            nextFriMidnight = nextFriMidnight.add(1, "week");
          }

          showTimeoutId = setTimeout(() => {
            sessionStorage.removeItem("autoavaliacaoClosed");
            fetchStatus();
          }, nextFriMidnight.diff(now));
        };

        const inPeriod =
          day === 5 ||
          day === 6 ||
          day === 0 ||
          day === 1 ||
          day === 2 ||
          day === 3 ||
          (day === 4 && hour < limitHourInWeek);

        if (answered) {
          setStatus("hidden");
          scheduleTimeouts();
          return;
        }

        if (inPeriod) {
          const closed = sessionStorage.getItem("autoavaliacaoClosed");
          setStatus(closed ? "button" : "open");
        } else {
          setStatus("hidden");
        }

        scheduleTimeouts();
        console.log({
          response: response.data,
          answered,
          day,
          hour,
          inPeriod,
          closed: sessionStorage.getItem("autoavaliacaoClosed"),
        });
      } catch (err) {
        console.error(err);
        setIsLoading(false);
        setStatus("hidden");
      }
    }

    if (colaborador_id) {
      fetchStatus();
    }

    return () => {
      if (hideTimeoutId) clearTimeout(hideTimeoutId);
      if (showTimeoutId) clearTimeout(showTimeoutId);
    };
  }, [colaborador_id, token]);

  function closeModal() {
    sessionStorage.setItem("autoavaliacaoClosed", "true");
    setStatus("button");
  }

  // Mantém a flag ao reabrir pelo botão flutuante: se a página for
  // recarregada com o modal aberto, ele volta como botão em vez de abrir
  // sozinho de novo.
  function openModal() {
    setStatus("open");
  }

  async function answerAutoavaliacao(
    data: {
      autoavaliacaocampo_id: number | null;
      resposta: number | null;
      observacao: string;
    }[],
  ) {
    setIsSubmitting(true);

    try {
      await responderAutoavaliacaoBatch(data);

      sessionStorage.removeItem("autoavaliacaoClosed");
      setStatus("hidden");
    } catch (err) {
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }

  return {
    status,
    closeModal,
    openModal,
    answerAutoavaliacao,
    isLoading,
    isSubmitting,
  };
}
